import { useState, useCallback, SyntheticEvent } from 'react';
import { Box, Tabs, Tab } from '@mui/material';
import TabPanel from './TabPanel';

export interface TabItem {
    label: string;
    id?: string;
    disabled?: boolean;
}

interface PageTabsProps {
    tabs: TabItem[];
    defaultTab?: number;
    activeTab?: number;
    ariaLabel?: string;
    onTabChange?: (tabIndex: number) => void;
    children: React.ReactNode[];
}

/**
 * Component that renders a list of tabs and their corresponding content.
 *
 * @param {PageTabsProps} props
 * @returns {JSX.Element}
 */
const PageTabs: React.FC<PageTabsProps> = (props) => {
    const { tabs, defaultTab = 0, activeTab: controlledTab, ariaLabel = 'Page tabs', onTabChange, children } = props;

    const [internalTab, setInternalTab] = useState<number>(defaultTab);
    const activeTab = controlledTab ?? internalTab;

    const handleTabChange = useCallback(
        (event: SyntheticEvent, newValue: number) => {
            setInternalTab(newValue);
            if (onTabChange) {
                onTabChange(newValue);
            }
        },
        [onTabChange]
    );

    return (
        <Box>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, bgcolor: 'background.paper' }}>
                <Tabs value={activeTab} onChange={handleTabChange} aria-label={ariaLabel}>
                    {tabs.map((tab, index) => (
                        <Tab
                            key={tab.id || `tab-${index}`}
                            id={`tab-${index}`}
                            label={tab.label}
                            disabled={tab.disabled}
                            aria-controls={`tabpanel-${index}`}
                        />
                    ))}
                </Tabs>
            </Box>
            {children.map((child, index) => (
                <TabPanel key={index} value={activeTab} index={index}>
                    {child}
                </TabPanel>
            ))}
        </Box>
    );
};

export default PageTabs;
